import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PRODUCTS } from "../data/site";
import ProductModal from "./ProductModal";
import MaskReveal from "./MaskReveal";

const EASE = [0.16, 1, 0.3, 1];

const SectionTitle = () => (
  <div className="mx-auto mb-14 max-w-2xl text-center">
    <motion.span
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.7, ease: EASE }}
      className="mb-4 inline-block rounded-full border-2 border-lilaclight bg-white px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.2em] text-lilac"
    >
      ✦ Nossos tesouros ✦
    </motion.span>
    <MaskReveal>
      <h2 className="font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
        Produtos que fazem os olhos brilharem
      </h2>
    </MaskReveal>
    <motion.p
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: 0.35 }}
      className="mt-3 font-hand text-2xl text-inksoft"
    >
      clica em qualquer um para se apaixonar
    </motion.p>
  </div>
);

const AgendaSlides = ({ images, title }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % images.length), 3500);
    return () => clearInterval(t);
  }, [images.length]);
  return (
    <>
      <AnimatePresence>
        <motion.img
          key={i}
          src={images[i]}
          alt={`${title} — foto ${i + 1}`}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: "easeInOut" }}
          className="absolute inset-0 h-full w-full object-cover"
          loading={i === 0 ? "eager" : "lazy"}
          decoding="async"
        />
      </AnimatePresence>
      <div className="absolute right-4 top-4 z-10 flex gap-1.5" data-testid={`slides-dots-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
        {images.map((_, d) => (
          <span key={d} className={`h-1.5 w-1.5 rounded-full transition-colors duration-300 ${d === i ? "bg-white" : "bg-white/45"}`} />
        ))}
      </div>
    </>
  );
};

const ProductCard = ({ p, i, onOpen }) => (
  <motion.article
    initial={{ opacity: 0, y: 44 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.75, ease: EASE, delay: (i % 3) * 0.09 }}
    onClick={() => onOpen(p)}
    onMouseMove={(e) => {
      const r = e.currentTarget.getBoundingClientRect();
      e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
      e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
    }}
    data-testid={`product-card-${p.id}`}
    className={`group relative cursor-pointer overflow-hidden rounded-[2rem] border-2 border-white bg-white shadow-[0_14px_40px_rgba(183,148,230,0.18)] transition-shadow duration-500 hover:shadow-[0_24px_60px_rgba(183,148,230,0.4)] ${p.span}`}
  >
    <div className={`relative w-full overflow-hidden ${p.featured ? "h-72 sm:h-96" : "h-72"}`}>
      {p.images && p.images.length > 1 ? (
        <AgendaSlides images={p.images} title={p.title} />
      ) : (
        <img
          src={p.img}
          alt={p.title}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" />

      <span className="absolute left-4 top-4 rounded-full bg-goldy px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider text-ink shadow-[0_6px_16px_rgba(229,184,66,0.5)]">
        {p.badge}
      </span>
      {p.demo && (
        <span className="absolute right-4 top-4 rounded-full bg-white/90 px-3 py-1.5 text-[11px] font-bold text-inksoft">
          foto ilustrativa
        </span>
      )}

      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-5 sm:p-6">
        <div>
          <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{p.title}</h3>
          <p className={`mt-1 max-w-md text-sm leading-snug text-white/85 ${p.featured ? "block" : "hidden lg:block"}`}>
            {p.desc.split("—")[0].split(".").slice(0, p.featured ? 2 : 1).join(".") + (p.featured ? "." : "")}
          </p>
        </div>
        <span className="grid h-11 w-11 shrink-0 translate-y-2 place-items-center rounded-full bg-white text-ink opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100" data-testid={`product-more-${p.id}`}>
          <ArrowUpRight size={18} />
        </span>
      </div>

      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{ background: "radial-gradient(340px circle at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,0.28), transparent 65%)" }}
      />
    </div>
  </motion.article>
);

export default function Products() {
  const [open, setOpen] = useState(null);

  return (
    <section id="produtos" className="relative py-20 sm:py-28">
      <div className="absolute left-1/2 top-0 -z-10 h-80 w-[42rem] max-w-full -translate-x-1/2 rounded-full bg-lilaclight/60 blur-[120px]" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionTitle />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-6">
          {PRODUCTS.map((p, i) => (
            <ProductCard key={p.id} p={p} i={i} onOpen={setOpen} />
          ))}
        </div>
      </div>
      <ProductModal product={open} onClose={() => setOpen(null)} />
    </section>
  );
}
